import { useLingui } from "@lingui/react/macro";
import { Anchor, List, Text, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";

import { DocHeading } from "@/components/docs/DocHeading.tsx";
import { DocsContent } from "@/components/docs/DocsContent.tsx";

export const Route = createFileRoute("/docs/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useLingui();

  return (
    <DocsContent>
      <Title order={1} size="h2">{t`Overview`}</Title>
      <Text>
        {t`Welcome to the Sweco REALity documentation and guides on how to use the platform. Use the navigation on the
        left to browse topics, and the table of contents on the right to jump between sections of the current page.
        If you don't find what you're looking for, please reach out to our support team for assistance. You find a link to the support form in the menu in the right top corner.`}
      </Text>

      <DocHeading order={2} size="h3">{t`What is REALity?`}</DocHeading>
      <Text>
        {t`REALity is Sweco’s platform for accessing geospatial and property-related information from official data
        sources, such as Lantmäteriet. For a web user, REALity mainly consists of two services:`}
      </Text>
      <List withPadding>
        <List.Item>
          <Anchor href="/docs/property-search">{t`Property Search`}</Anchor>
          {" – "}
          {t`the web-based replacement for Metria FastighetSök, used to search for and inspect property information from Lantmäteriet.`}
        </List.Item>
        <List.Item>
          {t`Data Downloads`}
          {" – "}
          {t`access to useful datasets and exports, primarily from Lantmäteriet and other approved official sources`}
        </List.Item>
      </List>

      <Text>
        {t`REALity is designed to act as a middle layer between official data providers and users, applications and
        assignments. Users normally access data through REALity, while REALity manages the connection to the underlying
        sources, access rights, usage tracking and cost allocation. The service can also be used by other applications,
        such as GIS systems and internal tools. Read more about `}
        <Anchor href="/docs/platform/access">{t`Integrate software with REALity`}</Anchor>
      </Text>

      <Text>
        {t`This centralised model reduces the need for each Sweco application or user group to maintain its own
        data-provider connections, authentication mechanisms, authorisation rules, billing and cost-allocation processes,
        and audit trails.`}
      </Text>

      <DocHeading
        order={2}
        size="h3"
      >{t`Replacing Metria FastighetSök`}</DocHeading>
      <Text>
        {t`REALity Property Search is intended to replace Metria FastighetSök for Sweco users. The purpose of the
        replacement is to provide Sweco with:`}
      </Text>
      <List withPadding>
        <List.Item>{t`Greater control over how property information is accessed`}</List.Item>
        <List.Item>{t`A more integrated experience with Sweco’s systems and other software`}</List.Item>
        <List.Item>{t`Improved access management through Sweco login`}</List.Item>
        <List.Item>{t`Better support for cost allocation to projects`}</List.Item>
        <List.Item>{t`A common platform for both manual searches and system integrations`}</List.Item>
        <List.Item>{t`Reduced dependence on external user interfaces and separate solutions`}</List.Item>
      </List>
      <Text>
        {t`The REALity web interface fulfils a similar role to FastighetSök: a user can search for a property, inspect
        relevant information and export or download the result.`}
      </Text>
    </DocsContent>
  );
}
